import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { QUERY_KEYS } from "@/services/api/queryKeys";
import { MUTATION_KEYS } from "@/services/api/mutationKeys";
import type {
  SsoProvider,
  CreateSsoProviderPayload,
  UpdateSsoProviderPayload,
} from "@/types";

export const useSsoProviders = (portalId: string) => {
  const queryClient = useQueryClient();

  // Query: list SSO providers for this portal
  const providersQuery = useQuery({
    queryKey: QUERY_KEYS.SSO_PROVIDERS.LIST(portalId),
    queryFn: async () => {
      const response = await apiClient.get<SsoProvider[]>(
        API_ENDPOINTS.SYSTEM.SSO_PROVIDERS.LIST(portalId)
      );
      return response.data;
    },
    enabled: !!portalId,
  });

  // Mutation: create SSO provider
  const createMutation = useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_SSO_PROVIDER],
    mutationFn: async (payload: CreateSsoProviderPayload) => {
      const response = await apiClient.post<SsoProvider>(
        API_ENDPOINTS.SYSTEM.SSO_PROVIDERS.ADD(portalId),
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SSO_PROVIDERS.ALL(portalId),
      });
    },
  });

  // Mutation: update SSO provider
  const updateMutation = useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_SSO_PROVIDER],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateSsoProviderPayload;
    }) => {
      const response = await apiClient.patch<SsoProvider>(
        API_ENDPOINTS.SYSTEM.SSO_PROVIDERS.UPDATE(portalId, id),
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SSO_PROVIDERS.ALL(portalId),
      });
    },
  });

  // Mutation: delete SSO provider
  const deleteMutation = useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_SSO_PROVIDER],
    mutationFn: async (id: string) => {
      await apiClient.delete(
        API_ENDPOINTS.SYSTEM.SSO_PROVIDERS.REMOVE(portalId, id)
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SSO_PROVIDERS.ALL(portalId),
      });
    },
  });

  return {
    providers: providersQuery.data ?? [],
    isLoading: providersQuery.isLoading,
    error: providersQuery.error,

    createProvider: createMutation.mutate,
    isCreating: createMutation.isPending,

    updateProvider: updateMutation.mutate,
    isUpdating: updateMutation.isPending,

    deleteProvider: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};
